import {useQuery} from '@apollo/client';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {sanitiseProductType} from '../../../utils/sanitiseProductType';
import validateUUID from '../../../utils/validateUUID';
import {BUILDER_CONTRIBUTE_POTS} from '../builderPotQueries';
import {reccNonEditable} from '../CreateBuilder/BuilderSourceDestination/CreateBuilderFooter';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {BuilderBankAccount} from '../../../schema-types/BuilderBankAccount';
import {
  BuilderContributePots,
  BuilderContributePotsVariables,
  BuilderContributePots_contributePots_sources_PotType,
  BuilderContributePots_contributePots_sources_BankAccountType,
} from '../../../schema-types/BuilderContributePots';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';

// A bank account owned by the contact's employer rather than the contact.
export type ContributeEmployerBankAccount = BuilderBankAccount & {
  account: {id: string; name: string | null} | null;
};

export const useContributionPotsQuery = (): {
  sourcePots: BuilderPot[];
  sourceBankAccounts: BuilderBankAccount[];
  employerBankAccounts: ContributeEmployerBankAccount[];
  destinationPots: BuilderPot[];
} => {
  const {builderDetails, builderReviewId} = useBuilderContext();
  const {values} = useAutoForm<builderFieldsType>();
  const contactId = values.contact ? validateUUID(values.contact) || null : null;
  const isNonEditable = !!reccNonEditable(builderDetails);

  const {data: potsData} = useQuery<
    BuilderContributePots,
    BuilderContributePotsVariables
  >(BUILDER_CONTRIBUTE_POTS, {
    variables: {
      reviewId: validateUUID(builderReviewId)!,
      contactId,
      potType: sanitiseProductType(values.mainPlanType) ?? '',
    },
    skip:
      isNonEditable ||
      values.planAction !== OVRecommendActionEnum.CONTRIBUTE ||
      !values.mainPlanType ||
      !validateUUID(builderReviewId),
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  if (isNonEditable) {
    const savedSource = builderDetails?.contribute?.source;
    const savedEmployerBankAccount =
      builderDetails?.contribute?.employerSourceBankAccount;
    const savedDestination = builderDetails?.contribute?.destinationPot;
    return {
      sourcePots: savedSource?.__typename === 'PotType' ? [savedSource] : [],
      sourceBankAccounts:
        savedSource?.__typename === 'BankAccountType' ? [savedSource] : [],
      employerBankAccounts: savedEmployerBankAccount ? [savedEmployerBankAccount] : [],
      // A CashPotType source needs no row data — the "New cash contribution"
      // row renders from the form state alone.
      destinationPots: savedDestination ? [savedDestination] : [],
    };
  }

  const sources = potsData?.contributePots?.sources ?? [];
  return {
    sourcePots: sources.filter(
      (s): s is BuilderContributePots_contributePots_sources_PotType =>
        s.__typename === 'PotType'
    ),
    sourceBankAccounts: sources.filter(
      (s): s is BuilderContributePots_contributePots_sources_BankAccountType =>
        s.__typename === 'BankAccountType'
    ),
    employerBankAccounts: potsData?.contributePots?.employerSources ?? [],
    destinationPots: potsData?.contributePots?.destinations ?? [],
  };
};
